import React, { useCallback, useEffect, useState } from 'react';
import { api, Notice, Section, AGENT_LABEL, formatWhen, tierLabel } from './ui';

/** What the agents did, and what it cost in tokens. */

const Activity = ({ team, onOpen }) => {
  const [events, setEvents] = useState([]);
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [e, u] = await Promise.all([api(`/teams/${team.id}/events?limit=200`), api(`/teams/${team.id}/usage`)]);
      setEvents(e.events || []);
      setUsage(u.usage || null);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [team.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const totalIn = (usage?.by_agent || []).reduce((s, r) => s + Number(r.input_tokens || 0), 0);
  const totalOut = (usage?.by_agent || []).reduce((s, r) => s + Number(r.output_tokens || 0), 0);
  const totalCached = (usage?.by_agent || []).reduce((s, r) => s + Number(r.cache_read_tokens || 0), 0);
  // Of the input, the share read from the prompt cache, at a tenth of the price.
  const cachedShare = (r) => (Number(r.input_tokens) ? `${Math.round((100 * Number(r.cache_read_tokens || 0)) / Number(r.input_tokens))}%` : '—');

  return (
    <div className="space-y-5">
      {error && <Notice tone="error">{error}</Notice>}
      <Section title="Model usage, last 30 days" hint={`${totalIn.toLocaleString()} input (${totalCached.toLocaleString()} of them from the cache) and ${totalOut.toLocaleString()} output tokens in total. Won: ${usage?.deals?.won || 0}, lost: ${usage?.deals?.lost || 0}.`}>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-gray-500 dark:text-slate-400">
              <tr><th className="py-1 pr-4">Agent</th><th className="py-1 pr-4">Runs on</th><th className="py-1 pr-4">Runs</th><th className="py-1 pr-4">Input tokens</th><th className="py-1 pr-4">From cache</th><th className="py-1 pr-4">Output tokens</th><th className="py-1">Failures</th></tr>
            </thead>
            <tbody className="text-gray-700 dark:text-slate-300">
              {(usage?.by_agent || []).map((r) => (
                <tr key={`${r.agent}-${r.model}`} className="border-t border-gray-100 dark:border-slate-800">
                  <td className="py-1 pr-4">{AGENT_LABEL[r.agent] || r.agent}</td>
                  {/* The tier, never the model id: see tierLabel in ui.jsx. */}
                  <td className="py-1 pr-4 text-xs">{tierLabel(r.model) || '—'}</td>
                  <td className="py-1 pr-4">{r.runs}</td>
                  <td className="py-1 pr-4">{Number(r.input_tokens || 0).toLocaleString()}</td>
                  <td className="py-1 pr-4">{cachedShare(r)}</td>
                  <td className="py-1 pr-4">{Number(r.output_tokens || 0).toLocaleString()}</td>
                  <td className="py-1">{r.failures}</td>
                </tr>
              ))}
              {!(usage?.by_agent || []).length && <tr><td colSpan={7} className="py-3 text-gray-500">No model calls yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Timeline" hint="Newest first.">
        <ul className="space-y-1 text-sm">
          {events.map((e) => (
            <li key={e.id} className="flex gap-3 border-b border-gray-100 py-1 dark:border-slate-800">
              <span className="w-40 flex-none text-xs text-gray-400">{formatWhen(e.created_at)}</span>
              <span className="w-32 flex-none text-xs font-medium text-gray-700 dark:text-slate-300">{AGENT_LABEL[e.agent] || e.agent}</span>
              <span className="text-gray-700 dark:text-slate-300">
                {e.domain && (
                  <button type="button" onClick={() => onOpen(e.opportunity_id)} className="mr-1 font-medium text-blue-700 hover:underline dark:text-blue-300">{e.domain}</button>
                )}
                {e.event.replace(/_/g, ' ')}{e.detail ? `: ${e.detail}` : ''}
              </span>
            </li>
          ))}
          {!events.length && <li className="text-gray-500">Nothing yet.</li>}
        </ul>
      </Section>
    </div>
  );
};

export default Activity;
